// Remote API wrapper that uses axiosClient
import axiosClient from "../api/axiosClient";
import { track } from "../utils/track";
import { prepareFile, MAX_RAW_BYTES } from "../utils/imageUpload";
import i18n from "../i18n";

// Auth functions
export async function signup(userData) {
  return axiosClient.post("/signup", userData);
}

export async function verifyCode({ user_id, code }) {
  return axiosClient.post("/verify-code", { user_id, code });
}

export async function login(email, password) {
  return axiosClient.post("/login", { email, password });
}

// User functions
export async function createUser(payload) {
  return axiosClient.post("/users", payload);
}

export async function getUser() {
  return axiosClient.get(`/user`);
}

export async function updateUser(patch) {
  return axiosClient.patch(`/user`, patch);
}

export async function requestPasswordReset({ email }) {
  return axiosClient.post(`/forgot-password`, { email });
}

// Phone transfer (when a phone already exists in another organization)
export async function requestPhoneTransferCode({ phone }) {
  return axiosClient.post(`/phone-transfer/request`, { phone });
}

export async function verifyPhoneTransferCode({ phone, code }) {
  return axiosClient.post(`/phone-transfer/verify`, { phone, code });
}

export async function getScanner(scannerId) {
  return axiosClient.get(`/scanners/${scannerId}`);
}

export async function listScanners() {
  return axiosClient.get(`/scanners`);
}

export async function createScanner(scanner) {
  return axiosClient.post(`/scanners`, scanner);
}

export async function updateScanner(scannerId, patch) {
  return axiosClient.post(`/scanners/${scannerId}`, patch);
}

export async function deleteScanner(scannerId) {
  return axiosClient.delete(`/scanners/${scannerId}`);
}

export async function getTokens() {
  return axiosClient.get(`/tokens`);
}

export async function addTokens(amount) {
  return axiosClient.post(`/tokens`, { amount });
}

// Error con mensaje para el usuario (las páginas muestran `err.message` si no hay `response.data.message`).
const tooLarge = () => {
  const err = new Error(i18n.t("uploadProcessor.too_large"));
  err.status = 413;
  return err;
};

export async function processUpload(scannerId, originalFile) {
  // Las fotos grandes se recomprimen en el navegador; lo que aun así no cabe (p. ej. un PDF de 8 MB) se explica
  // aquí en vez de dejar que el servidor responda 413 sin más (límite de la Lambda síncrona: ~4,4 MB reales).
  const { file, resized } = await prepareFile(originalFile);
  if (resized) track("upload_resized", resized);
  if (file.size > MAX_RAW_BYTES) throw tooLarge();

  // Build form data
  const form = new FormData();
  form.append("file", file);
  try {
    return await axiosClient.post(`/scanners/${scannerId}/process`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      // Manual uploads pueden tardar más (Textract + OpenAI), ampliamos timeout específico
      timeout: 600000,
    });
  } catch (err) {
    if (err?.response?.status === 413) throw tooLarge();
    throw err;
  }
}

export async function getScannerHistory(scannerId, params = {}) {
  return axiosClient.get(`/scanners/${scannerId}/history`, {
    params,
  });
}

export async function getUsageSummary(days = 30, sourceFilter = "all") {
  const params = { days };
  if (sourceFilter && sourceFilter !== "all") {
    // Normalize phone input to avoid issues with leading '+' encoding
    params.source =
      typeof sourceFilter === "string"
        ? sourceFilter.replace(/^\+/, "")
        : sourceFilter;
  }
  return axiosClient.get(`/usage/summary`, { params });
}

// ============= Support Tickets =============
export async function createTicket({ subject, message, priority }) {
  return axiosClient.post(`/tickets`, { subject, message, priority });
}

export async function listTickets(params = {}) {
  return axiosClient.get(`/tickets`, { params });
}

export async function getTicket(ticketId) {
  return axiosClient.get(`/tickets/${ticketId}`);
}

export async function postTicketMessage(ticketId, message) {
  return axiosClient.post(`/tickets/${ticketId}/messages`, { message });
}

// ============= Subscription & Billing Functions =============

// Get current subscription details
export async function getSubscription() {
  return axiosClient.get(`/subscription`);
}

// Get pricing catalog (plans + credit packs)
export async function getSubscriptionPricing() {
  return axiosClient.get(`/subscription/pricing`);
}

// Activate free plan
export async function activateFreePlan() {
  return axiosClient.post(`/subscription/free`);
}

// Create Stripe Checkout session for subscription
export async function createCheckoutSession(payload) {
  return axiosClient.post(`/subscription/checkout`, payload);
}

// Create Stripe Checkout session for credit pack purchase
export async function createCreditPackCheckout(payload) {
  return axiosClient.post(`/subscription/credits/checkout`, payload);
}

// Fix accounts where initial free credits were incorrectly assigned to pack_credits_balance
export async function fixFreeCredits() {
  return axiosClient.post(`/subscription/fix-free-credits`);
}

// Save billing details to company config
export async function saveCompanyBilling(billingInfo) {
  return axiosClient.post(`/subscription/billing`, billingInfo);
}

// Cancel subscription
export async function cancelSubscription() {
  return axiosClient.post(`/subscription/cancel`);
}

// Get usage history
export async function getUsageHistory(params = {}) {
  return axiosClient.get(`/usage/history`, { params });
}

// Get invoices
export async function getInvoices() {
  return axiosClient.get(`/subscription/invoices`);
}

// Change subscription plan
export async function changePlan({ priceId, planId, billingCycle }) {
  return axiosClient.put(`/subscription/change-plan`, {
    priceId,
    planId,
    billingCycle,
  });
}

// Get Stripe customer portal URL (for managing payment methods, invoices, etc.)
export async function getCustomerPortalUrl() {
  return axiosClient.post(`/subscription/portal`);
}

// ============= API Key Management =============

// Get current API key
export async function getReferrals() {
  return axiosClient.get("/referrals");
}

export async function getApiKey() {
  return axiosClient.get(`/api-keys`);
}

// Regenerate API key
export async function regenerateApiKey() {
  return axiosClient.post(`/api-keys/regenerate`);
}

// Scanner AI Assistant
export async function callScannerAssistant({
  user_input,
  conversation_history,
  language,
}) {
  return axiosClient.post(`/scanner-assistant`, {
    user_input,
    conversation_history,
    language,
  });
}

const remoteApi = {
  // Auth
  signup,
  verifyCode,
  login,
  // Users
  createUser,
  getUser,
  updateUser,
  requestPasswordReset,
  requestPhoneTransferCode,
  verifyPhoneTransferCode,
  // Scanners
  listScanners,
  getScanner,
  createScanner,
  updateScanner,
  deleteScanner,
  // Tokens
  getTokens,
  addTokens,
  // Processing
  processUpload,
  getScannerHistory,
  getUsageSummary,
  callScannerAssistant,
  // Tickets
  createTicket,
  listTickets,
  getTicket,
  postTicketMessage,
  // Subscription & Billing
  getSubscription,
  getSubscriptionPricing,
  activateFreePlan,
  createCheckoutSession,
  saveCompanyBilling,
  createCreditPackCheckout,
  cancelSubscription,
  getUsageHistory,
  getInvoices,
  changePlan,
  getCustomerPortalUrl,
  // Referrals
  getReferrals,
  // API Keys
  getApiKey,
  regenerateApiKey,
};

/**
 * Eventos del pixel propio (utils/track.js) en los puntos que importan para el negocio:
 * activación (subidas, plantillas) y dinero (checkout, cancelaciones). Se hace aquí y no en cada
 * página para no perder ninguna ruta de entrada. Nunca altera el resultado ni el error original.
 */
const instrument = (name, fn, describe = () => ({})) =>
  async (...args) => {
    const t0 = Date.now();
    let props = {};
    try {
      props = describe(...args) || {};
    } catch {
      // describir no debe romper la llamada
    }
    track(`${name}_start`, props);
    try {
      const out = await fn(...args);
      track(`${name}_success`, { ...props, ms: Date.now() - t0 });
      return out;
    } catch (err) {
      track(`${name}_error`, { ...props, status: err?.response?.status || err?.status || 0, ms: Date.now() - t0 });
      throw err;
    }
  };

const ext = (f) => (String(f?.name || "").split(".").pop() || "").toLowerCase().slice(0, 8);

remoteApi.processUpload = instrument("upload", processUpload, (id, file) => ({
  kb: Math.round((file?.size || 0) / 1024),
  type: ext(file),
  scanner: String(id).slice(0, 8),
}));
remoteApi.createCheckoutSession = instrument("checkout", createCheckoutSession, (p) => ({
  kind: "plan",
  item: p?.planId,
  cycle: p?.billingCycle,
}));
remoteApi.createCreditPackCheckout = instrument("checkout", createCreditPackCheckout, (p) => ({
  kind: "pack",
  item: p?.packId,
}));
remoteApi.createScanner = instrument("scanner_create", createScanner);
remoteApi.activateFreePlan = instrument("free_plan", activateFreePlan);
remoteApi.cancelSubscription = instrument("cancel_subscription", cancelSubscription);
remoteApi.changePlan = instrument("plan_change", changePlan, (p) => ({ item: p?.planId, cycle: p?.billingCycle }));
remoteApi.getCustomerPortalUrl = instrument("billing_portal", getCustomerPortalUrl);

export default remoteApi;
